import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ORDER_STATUS_LABELS_LAO,
  type OrderChannel,
  type OrderPickupType,
} from "@/modules/orders/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BanknoteIcon,
  Building2Icon,
  PackageIcon,
  PhoneIcon,
  PrinterIcon,
  TruckIcon,
  UserIcon,
} from "lucide-react";
import { useOrderDetailQuery } from "../api/queries";
import { openOrderBillPrint } from "../lib/orderBillPrint";

const CHANNEL_LABELS: Record<OrderChannel, string> = {
  APP: "ແອັບ",
  WALKIN: "ເຂົ້າຮ້ານ",
};

const PICKUP_LABELS: Record<OrderPickupType, string> = {
  ASAP: "ຮັບທັນທີ",
  SLOT: "ນັບຮັບຕາມເວລາ",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  DRAFT: "outline",
  CONFIRMED: "secondary",
  PREPARING: "default",
  READY: "default",
  COMPLETED: "secondary",
  CANCELLED: "destructive",
};

export function OrderDetailPage() {
  const nav = useNavigate({ from: "/app/orders/$id" });
  const { id } = useParams({ from: "/app/orders/$id" });
  const { data: order, ...queryResult } = useOrderDetailQuery(id ?? "");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ອໍເດີ (HQ)",
              to: "/app/orders",
            },
            {
              label: "ລາຍລະອຽດອໍເດີ",
            },
          ]}
        />
      </Header>
      <Main>
        <QueryState
          result={queryResult}
          title="ກຳລັງໂຫຼດລາຍລະອຽດອໍເດີ"
          description="ກຳລັງດຶງຂໍ້ມູນ"
          variant="fullscreen"
        >
          {!order ? null : (
            <div className="space-y-6">
              {/* Order header */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex flex-col items-start">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="font-bold text-2xl tracking-tight">
                      Order #{order.orderNo}
                    </h1>
                    <Badge
                      variant={
                        STATUS_BADGE_VARIANTS[order.status] ?? "secondary"
                      }
                    >
                      {ORDER_STATUS_LABELS_LAO[order.status]}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap items-center gap-6 text-muted-foreground text-sm">
                    <span className="flex items-center gap-2">
                      <Building2Icon className="size-4" />
                      {order.branchName}
                    </span>
                    <span className="flex items-center gap-2">
                      <PhoneIcon className="size-4" />
                      {CHANNEL_LABELS[order.channel]}
                    </span>
                    <span className="flex items-center gap-2">
                      <BanknoteIcon className="size-4" />
                      ຍອດ:{" "}
                      {order.total.toLocaleString("lo-LA", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}{" "}
                      {order.currency}
                    </span>
                    <span>ວັນທີສ້າງ: {formatDateTimeLocal(order.createdAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => nav({ to: "/app/orders" })}
                  >
                    <ArrowLeftIcon className="size-4" />
                    ກັບຄືນ
                  </Button>
                  <Button
                    type="button"
                    onClick={() => openOrderBillPrint(order)}
                  >
                    <PrinterIcon className="size-4" />
                    ພິມ
                  </Button>
                  {/* <Button
                    variant="secondary"
                    type="button"
                    onClick={() => window.print()}
                  >
                    <RefreshCwIcon className="size-4" />
                    ປ່ຽນສາຂາ (ໃນໄລຍະຕໍ່ໄປ)
                  </Button> */}
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-3">
                {/* Left column: Customer, Logistics, Order items */}
                <div className="grid gap-6 sm:grid-cols-2 lg:col-span-2">
                  <Card className="gap-0 rounded-xl border bg-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 font-semibold text-base">
                        <UserIcon className="size-4" />
                        ຂໍ້ມູນລູກຄ້າ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      {order.customerId ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <div>
                            <span className="text-muted-foreground">ຊື່: </span>
                            {order.customerName ?? "–"}
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              ເບີໂທ:{" "}
                            </span>
                            {order.customerPhone ?? "–"}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">
                          ບໍ່ມີຂໍ້ມູນລູກຄ້າ (ເຂົ້າຮ້ານ)
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="gap-0 rounded-xl border bg-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 font-semibold text-base">
                        <TruckIcon className="size-4" />
                        ການຮັບສິນຄ້າ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <span className="text-muted-foreground">
                            ປະເພດຮັບ:{" "}
                          </span>
                          {PICKUP_LABELS[order.pickupType]}
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            ວັນນັບຮັບ:{" "}
                          </span>
                          {order.pickupAt
                            ? formatDateTimeLocal(order.pickupAt)
                            : "–"}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="col-span-2 rounded-xl border bg-card">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="flex items-center gap-2 font-semibold text-base">
                        <PackageIcon className="size-4" />
                        ລາຍການສິນຄ້າ
                      </CardTitle>
                      <span className="text-muted-foreground text-sm">
                        {order.items.length} ລາຍການ
                      </span>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="pb-2 font-medium">ສິນຄ້າ</th>
                              <th className="w-16 pb-2 text-right font-medium">
                                ຈຳນວນ
                              </th>
                              <th className="w-24 pb-2 text-right font-medium">
                                ລາຄາ/ຫນ່ວຍ
                              </th>
                              <th className="w-24 pb-2 text-right font-medium">
                                ຍອດລາຍການ
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.items.map((item) => (
                              <tr key={item.id} className="border-b">
                                <td className="py-2">
                                  <div className="flex items-start gap-3">
                                    <div className="size-12 shrink-0 overflow-hidden rounded-md border bg-muted">
                                      {item.productImageUrl ? (
                                        <img
                                          src={resolveImageSrc(
                                            item.productImageUrl ?? undefined,
                                          ) ?? ""}
                                          alt=""
                                          className="size-full object-cover"
                                        />
                                      ) : (
                                        <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                                          ບໍ່ມີຮູບ
                                        </div>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="font-medium">
                                        {item.productName}
                                        {item.variantName &&
                                          item.variantName !== "Default" && (
                                            <span className="font-normal text-muted-foreground">
                                              {" "}
                                              ({item.variantName})
                                            </span>
                                          )}
                                      </div>
                                      {item.modifiers.length > 0 && (
                                        <ul className="mt-1 list-inside list-disc text-muted-foreground text-xs">
                                          {item.modifiers.map((m, i) => (
                                            <li
                                              key={`${item.id}-${m.name}-${m.priceDelta}-${i}`}
                                            >
                                              + {m.name}
                                              {m.priceDelta !== 0 &&
                                                ` (${m.priceDelta.toLocaleString("lo-LA")} ${order.currency})`}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-2 text-right">{item.qty}</td>
                                <td className="py-2 text-right">
                                  {item.unitPrice.toLocaleString("lo-LA", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </td>
                                <td className="py-2 text-right">
                                  {item.lineTotal.toLocaleString("lo-LA", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end">
                        <div className="mt-4 max-w-xs space-y-1 pt-4 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              ຍອດລວມ:
                            </span>
                            <span>
                              {order.subtotal.toLocaleString("lo-LA", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              {order.currency}
                            </span>
                          </div>
                          {order.discountTotal > 0 && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">
                                ສ່ວນລົດ:
                              </span>
                              <span>
                                -{order.discountTotal.toLocaleString("lo-LA")}{" "}
                                {order.currency}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between border-t pt-2 font-semibold">
                            <span>ຍອດສຸດທິ:</span>
                            <span>
                              {order.total.toLocaleString("lo-LA", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              {order.currency}
                            </span>
                          </div>
                          <div className="flex justify-between text-muted-foreground text-xs">
                            <span>ຊຳລະແລ້ວ:</span>
                            <span>
                              {order.paidTotal.toLocaleString("lo-LA", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              {order.currency}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right column: Timeline */}
                <div className="lg:col-span-1">
                  <Card className="rounded-xl border bg-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-semibold text-base">
                        ໄລຍະເວລາອໍເດີ
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="relative space-y-0">
                        {order.statusEvents.length === 0 ? (
                          <p className="text-muted-foreground text-sm">
                            ຍັງບໍ່ມີເຫດການ
                          </p>
                        ) : (
                          order.statusEvents.map((ev, i) => (
                            <div
                              key={ev.id}
                              className="relative flex gap-4 pb-6 last:pb-0"
                            >
                              {i < order.statusEvents.length - 1 && (
                                <span
                                  className="absolute top-5 left-[7px] h-full w-px bg-border"
                                  aria-hidden
                                />
                              )}
                              <div className="relative z-10 flex size-4 shrink-0 rounded-full border-2 border-primary bg-background" />
                              <div className="min-w-0 flex-1 pt-0.5 text-sm">
                                <div className="font-medium">
                                  {ORDER_STATUS_LABELS_LAO[
                                    ev.toStatus as keyof typeof ORDER_STATUS_LABELS_LAO
                                  ] ?? ev.toStatus}
                                </div>
                                <div className="mt-0.5 text-muted-foreground text-xs">
                                  {formatDateTimeLocal(ev.at)}
                                </div>
                                {ev.byUserDisplayName && (
                                  <div className="mt-0.5 text-muted-foreground text-xs">
                                    {ev.byUserDisplayName}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
